import { AuthError, useAuth } from '@/lib'
import { registerInputDataSchema } from '@jobilla/nexus-sdk'
import { useForm } from '@tanstack/react-form'
import { Link, useSearch } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { validationErrorMessages } from '../../rapid-ui'
import { logger } from '../../utilities'
import { AuthDivider } from './auth-divider'
import { AuthErrorAlert } from './auth-error-alert'
import { AuthInput } from './auth-input'
import { AuthSubmitButton } from './auth-submit-button'
import { GoogleSignInButton } from './google-sign-in-button'
import { JobillaBrandLogo } from './jobilla-brand-logo'
import { RegistrationVerificationForm } from './registration-verification-form'

const signUpSchema = registerInputDataSchema
	.extend({ confirmPassword: z.string() })
	.refine((data) => data.password === data.confirmPassword, {
		message: 'Passwords do not match',
		path: ['confirmPassword'],
	})

interface EmailVerificationState {
	email: string
	password: string
}

/** Maps API error response to field-level errors keyed by field name. */
function mapServerErrors(err: AuthError): Record<string, string> {
	const data = err.data
	if (!data || !('errors' in data) || !data.errors) {
		return {}
	}

	const fieldErrors: Record<string, string> = {}
	const rawErrors: unknown = data.errors

	// Laravel validation errors come keyed by field name in snake_case
	// e.g. { errors: { firstName: ["required"], lastName: ["required"] } }
	if (rawErrors && typeof rawErrors === 'object' && !Array.isArray(rawErrors)) {
		for (const [field, messages] of Object.entries(rawErrors)) {
			if (Array.isArray(messages)) {
				const text = messages.filter((m): m is string => typeof m === 'string').join(' ')
				if (text) {
					fieldErrors[field] = text
				}
			}
		}
		return fieldErrors
	}

	// WorkOS-style errors: array of { code, message, suggestions }
	// Password-related errors map to the password field
	if (Array.isArray(rawErrors)) {
		const passwordCodes = ['password_strength_error', 'password_too_short', 'password_pwned', 'password_too_weak']
		const passwordMessages: string[] = []
		const otherMessages: string[] = []

		for (const entry of rawErrors) {
			const e = entry as Record<string, unknown>
			const parts = [e.message].filter((p): p is string => typeof p === 'string')
			if (Array.isArray(e.suggestions)) {
				for (const s of e.suggestions) {
					if (typeof s === 'string') { parts.push(s) }
				}
			}
			const text = parts.join(' ')
			if (!text) { continue }

			if (typeof e.code === 'string' && passwordCodes.includes(e.code)) {
				passwordMessages.push(text)
			} else {
				otherMessages.push(text)
			}
		}

		if (passwordMessages.length > 0) {
			fieldErrors.password = passwordMessages.join(' ')
		}
		if (otherMessages.length > 0) {
			fieldErrors._form = otherMessages.join(' ')
		}
		return fieldErrors
	}

	return {}
}

export function SignUpForm() {
	const { t } = useTranslation('common')
	const { signUp, isLoading: isAuthLoading } = useAuth()
	const { redirect: redirectTo } = useSearch({ strict: false }) as { redirect?: string }
	const [verification, setVerification] = useState<EmailVerificationState | null>(null)
	const [formError, setFormError] = useState<string | null>(null)

	const form = useForm({
		defaultValues: {
			firstName: '',
			lastName: '',
			email: '',
			password: '',
			confirmPassword: '',
		},
		validators: {
			onChange: signUpSchema,
		},
		onSubmit: async ({ value }) => {
			setFormError(null)

			try {
				await signUp(value.email, value.password, value.firstName, value.lastName)
				setVerification({ email: value.email, password: value.password })
			} catch (err) {
				if (err instanceof AuthError) {
					const fieldErrors = mapServerErrors(err)
					logger.warn('Sign-up server error', {
						category: 'auth',
						data: {
							status: err.status,
							code: err.data && 'code' in err.data ? err.data.code : undefined,
							fieldErrors,
						},
					})
					if (Object.keys(fieldErrors).length > 0) {
						for (const [field, message] of Object.entries(fieldErrors)) {
							if (field === '_form') {
								setFormError(message)
							} else {
								form.setFieldMeta(field as keyof typeof value, (prev) => ({
									...prev,
									errorMap: { ...prev.errorMap, onSubmit: message },
								}))
							}
						}
					} else {
						setFormError(err.message)
					}
					return
				}

				logger.error(err, { category: 'auth', data: { action: 'sign-up' } })
				setFormError(t('auth.signUp.errorDefault'))
			}
		},
	})

	if (verification) {
		return (
			<RegistrationVerificationForm
				email={verification.email}
				password={verification.password}
			/>
		)
	}

	return (
		<div className="w-full max-w-xl mx-auto px-8 py-16">
			<JobillaBrandLogo />

			<div className="pt-6">
				<h1 className="text-2xl font-semibold text-gray-800 tracking-[0.01em]">{t('auth.signUp.title')}</h1>
			</div>

			<form
				onSubmit={(e) => {
					e.preventDefault()
					e.stopPropagation()
					void form.handleSubmit()
				}}
				className="mt-6 space-y-8"
			>
				<AuthErrorAlert message={formError} />

				<div className="grid grid-cols-2 gap-4">
					<form.Field name="firstName">
						{(field) => (
							<AuthInput
								label={t('auth.signUp.firstNameLabel', 'First name')}
								type="text"
								placeholder={t('auth.signUp.firstNamePlaceholder', 'First name')}
								value={field.state.value}
								onChange={(val) => field.handleChange(val)}
								autoComplete="given-name"
								error={validationErrorMessages(field.state.meta.errors).join(' ')}
							/>
						)}
					</form.Field>

					<form.Field name="lastName">
						{(field) => (
							<AuthInput
								label={t('auth.signUp.lastNameLabel', 'Last name')}
								type="text"
								placeholder={t('auth.signUp.lastNamePlaceholder', 'Last name')}
								value={field.state.value}
								onChange={(val) => field.handleChange(val)}
								autoComplete="family-name"
								error={validationErrorMessages(field.state.meta.errors).join(' ')}
							/>
						)}
					</form.Field>
				</div>

				<form.Field name="email">
					{(field) => (
						<AuthInput
							label={t('auth.signUp.workEmailLabel')}
							type="email"
							placeholder={t('auth.signUp.emailPlaceholder')}
							value={field.state.value}
							onChange={(val) => field.handleChange(val)}
							required={true}
							autoComplete="email"
							error={validationErrorMessages(field.state.meta.errors).join(' ')}
						/>
					)}
				</form.Field>

				<form.Field name="password">
					{(field) => (
						<AuthInput
							label={t('auth.signUp.passwordLabel', 'Password')}
							type="password"
							placeholder=""
							value={field.state.value}
							onChange={(val) => field.handleChange(val)}
							autoComplete="new-password"
							error={validationErrorMessages(field.state.meta.errors).join(' ')}
						/>
					)}
				</form.Field>

				<form.Field name="confirmPassword">
					{(field) => (
						<AuthInput
							label={t('auth.signUp.confirmPasswordLabel', 'Confirm password')}
							type="password"
							placeholder=""
							value={field.state.value}
							onChange={(val) => field.handleChange(val)}
							autoComplete="new-password"
							error={validationErrorMessages(field.state.meta.errors).join(' ')}
						/>
					)}
				</form.Field>

				<form.Subscribe>
					{(state) => (
						<AuthSubmitButton isLoading={state.isSubmitting || isAuthLoading}>
							{t('auth.signUp.submit')}
						</AuthSubmitButton>
					)}
				</form.Subscribe>

				<AuthDivider />

				<GoogleSignInButton mode="signUp" />

				<p className="text-center text-sm text-gray-600">
					{t('auth.signUp.hasAccount')}{' '}
					<Link
						to="/auth"
						search={redirectTo ? { redirect: redirectTo } : {}}
						className="text-[#6366F1] hover:text-[#4F46E5] font-medium"
					>
						{t('auth.signUp.signInLink')}
					</Link>
				</p>
			</form>
		</div>
	)
}
